import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { usePageMeta } from '@/lib/usePageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/speaking`;
const money = (c, cur = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur.toUpperCase() }).format((c || 0) / 100);

export function SpeakingAgreement() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  usePageMeta({ title: 'Speaking Engagement Agreement | Toia Wallace', path: '/speaking/agreement', robots: 'noindex, follow' });

  const load = () => axios.get(`${API}/agreement/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'This link is no longer valid.'));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async () => {
    if (!agreed) { toast.error('Please check the acknowledgment box.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/agreement/${token}`, { agreement_accepted: true });
      if (r.data?.deposit_url) window.location.href = r.data.deposit_url;
      else load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed.'); setBusy(false); }
  };

  if (err) return <PageShell><h1 className="font-serif-display text-3xl text-matte">{err}</h1></PageShell>;
  if (!d) return <PageShell><div className="text-taupe">Loading…</div></PageShell>;
  const t = d.terms || {};
  const cur = t.currency || 'usd';

  return (
    <div className="bg-ivory" data-testid="speaking-agreement">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-16">
        <div className="mx-auto max-w-[820px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Speaking Engagement · Agreement</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tightest text-matte">
            {d.event_name || 'Your event'}.
          </h1>
          <p className="mt-4 text-matte/70">For <strong>{d.organization || d.name}</strong></p>

          <div className="mt-10 bg-white border border-taupe/20 rounded-2xl p-6 sm:p-8 space-y-6">
            <div>
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Engagement Summary</div>
              <div className="grid sm:grid-cols-2 gap-3 text-[14px]">
                <Row label="Event Date" value={t.event_date} />
                <Row label="Location" value={t.event_location} />
                <Row label="Format" value={t.speaking_format} />
                <Row label="Duration" value={t.speaking_duration} />
              </div>
            </div>

            <div className="border-t border-taupe/15 pt-6">
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Fees</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Fee label="Speaking Fee" value={money(t.speaking_fee_cents, cur)} />
                <Fee label="Deposit" value={money(t.deposit_cents, cur)} accent />
                <Fee label="Balance" value={money(t.balance_cents, cur)} />
              </div>
            </div>

            {(t.travel_terms || t.accommodation_terms) && (
              <div className="border-t border-taupe/15 pt-6">
                <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Travel & Accommodation</div>
                {t.travel_terms && <p className="text-[14px] text-matte/80 mb-2">{t.travel_terms}</p>}
                {t.accommodation_terms && <p className="text-[14px] text-matte/80">{t.accommodation_terms}</p>}
              </div>
            )}

            {(t.cancellation_terms || t.additional_terms) && (
              <div className="border-t border-taupe/15 pt-6">
                <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Additional Terms</div>
                {t.cancellation_terms && <p className="text-[14px] text-matte/80 mb-2"><strong>Cancellation:</strong> {t.cancellation_terms}</p>}
                {t.additional_terms && <p className="text-[14px] text-matte/80 whitespace-pre-wrap">{t.additional_terms}</p>}
              </div>
            )}

            <div className="border-t border-taupe/15 pt-6">
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">Applicable Policies</div>
              <p className="text-[13px] text-matte/70 leading-relaxed mb-3">
                This engagement is governed by the brand-wide policies below. Any executed written speaking agreement between the parties supplements or supersedes these general policies for this engagement to the extent it differs from them.
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  { to: '/terms', label: 'Terms & Conditions' },
                  { to: '/no-refund-policy', label: 'No Refund Policy' },
                  { to: '/rescheduling-cancellation-policy', label: 'Rescheduling & Cancellation Policy' },
                ].map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`policy-link-${p.to.replace(/\//g, '')}`}
                      className="flex items-center justify-between gap-3 min-h-[44px] px-4 py-2.5 bg-ivory border border-taupe/20 rounded-lg text-matte hover:border-gold hover:text-gold transition-colors text-[13px] sm:text-[14px]"
                    >
                      <span>{p.label}</span>
                      <span aria-hidden className="text-taupe">↗</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <label className="flex items-start gap-3 cursor-pointer p-3 sm:p-4 bg-ivory rounded-lg border border-taupe/15 hover:border-gold/40 min-h-[44px]">
                <input
                  type="checkbox" checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={d.already_acknowledged}
                  data-testid="speaking-agreement-checkbox"
                  className="mt-1 w-4 h-4 accent-gold shrink-0"
                />
                <span className="text-matte text-[13px] sm:text-[14px] leading-snug">
                  I confirm the speaking engagement terms above and agree to the applicable Terms & Conditions, No Refund Policy, and Rescheduling & Cancellation Policy.
                </span>
              </label>
            </div>

            {!d.already_acknowledged ? (
              <button
                onClick={submit}
                disabled={!agreed || busy}
                data-testid="speaking-agreement-submit"
                className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? 'Continuing…' : 'Acknowledge & Continue to Deposit'}
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            ) : (
              <div className="text-center pt-2">
                <p className="text-emerald-700 mb-3">✓ Agreement acknowledged</p>
                {d.deposit_url && (
                  <a href={d.deposit_url} className="btn-primary">Continue to Deposit Payment<ArrowRight size={14} strokeWidth={1.6} /></a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export function SpeakingPay() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  usePageMeta({ title: 'Speaking Payment | Toia Wallace', path: '/speaking/pay', robots: 'noindex, follow' });

  useEffect(() => { axios.get(`${API}/pay/${token}`).then((r) => setG(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Invalid link.')); }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pay/${token}`);
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error('Payment gateway error.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Payment failed.'); setBusy(false); }
  };

  if (err) return <PageShell><h1 className="font-serif-display text-3xl text-matte">{err}</h1></PageShell>;
  if (!g) return <PageShell><div className="text-taupe">Loading…</div></PageShell>;

  return (
    <div className="bg-ivory" data-testid="speaking-pay">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-20">
        <div className="mx-auto max-w-[720px] px-6 sm:px-10">
          <div className="eyebrow text-taupe mb-6">Speaking · {g.kind === 'deposit' ? 'Deposit Payment' : 'Balance Payment'}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-[0.98] tracking-tightest text-matte break-words">
            {g.event_name || 'Your engagement'}.
          </h1>
          <p className="mt-4 text-matte/70 break-words">For <strong>{g.organization || g.name}</strong>{g.event_date ? ` · ${g.event_date}` : ''}</p>

          <div className="mt-10 bg-white border border-taupe/20 rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-6 border-b border-taupe/15">
              <div>
                <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-2">{g.kind === 'deposit' ? 'Deposit Due' : 'Remaining Balance Due'}</div>
                <div className="text-matte/70 text-sm">Secure payment via Stripe</div>
              </div>
              <div className="font-serif-display text-4xl sm:text-5xl text-matte tabular-nums">{money(g.amount_cents, g.currency)}</div>
            </div>

            {g.already_paid ? (
              <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-[14px]">
                ✓ This payment has already been received. Thank you.
              </div>
            ) : (
              <button
                onClick={pay}
                disabled={busy}
                data-testid="speaking-pay-continue"
                className="btn-primary mt-8 w-full justify-center bg-matte text-ivory hover:bg-[#1a1a1a] disabled:opacity-40"
              >
                {busy ? 'Redirecting to Stripe…' : `Pay ${money(g.amount_cents, g.currency)} Securely`}
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            )}
            <p className="text-taupe text-[11px] mt-4 text-center">Card details are processed by Stripe and never touch our servers.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export function SpeakingPaid() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  usePageMeta({ title: 'Payment Received | Toia Wallace', path: '/speaking/paid', robots: 'noindex, follow' });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/pay/${token}`);
        if (!cancelled) setG(r.data);
      } catch { /* ignore */ }
    };
    tick();
    // Poll a few times in case webhook lags
    const interval = setInterval(tick, 3000);
    const timeout = setTimeout(() => clearInterval(interval), 30000);
    return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
  }, [token]);

  const paid = g?.already_paid;
  const kind = g?.kind;

  return (
    <div className="bg-ivory" data-testid="speaking-paid">
      <section className="pt-32 sm:pt-40 lg:pt-52 pb-20">
        <div className="mx-auto max-w-[720px] px-6 sm:px-10 text-center">
          <div className="eyebrow text-taupe mb-6">Speaking · {kind === 'balance' ? 'Balance' : 'Deposit'} Payment</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tightest text-matte">
            {paid ? 'Payment received.' : 'Confirming your payment…'}
          </h1>
          <p className="mt-6 text-matte/70 max-w-[540px] mx-auto">
            {paid
              ? `Thank you. Your ${kind === 'balance' ? 'balance' : 'deposit'} payment has been received and your booking with Toia Wallace is confirmed. A confirmation email is on its way to your inbox.`
              : 'Stripe is confirming your payment. This page will refresh automatically. If it doesn\u2019t update within a minute, please check your email.'}
          </p>
          {g?.event_name && (
            <p className="mt-4 text-matte/60 text-sm">
              <strong>{g.event_name}</strong>{g.event_date ? ` · ${g.event_date}` : ''}
            </p>
          )}
          <div className="mt-10">
            <Link to="/" className="btn-primary">Return home<ArrowRight size={14} strokeWidth={1.6} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PageShell({ children }) {
  return <div className="bg-ivory min-h-[70vh] pt-40 px-6 text-center">{children}</div>;
}
function Row({ label, value }) {
  return (<div><div className="uppercase tracking-[0.18em] text-[9px] text-taupe mb-1">{label}</div><div className="text-matte/85 break-words">{value || '—'}</div></div>);
}
function Fee({ label, value, accent }) {
  return (
    <div className={`p-3 rounded-lg ${accent ? 'bg-gold/10 border border-gold/30' : 'bg-taupe/5'}`}>
      <div className="uppercase tracking-[0.18em] text-[9px] text-taupe mb-1">{label}</div>
      <div className={`font-serif-display text-lg sm:text-xl ${accent ? 'text-gold' : 'text-matte'} tabular-nums`}>{value}</div>
    </div>
  );
}
